import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { locationListSortOptionByValue, LocationListSortOptionValues } from 'models/SortByOptions';
import { nameof } from 'utils/typeHelpers';

export type LocationsState = {
  allLocationIds: number[];
  byId: Record<number, dto.GetGeonameModel>;
  isLoading: boolean;
  isEditing: boolean;
  hasError: boolean;
  canDelete: boolean;
  isAddInProgress: boolean;
  isUpdateInProgress: boolean;
  deleteInProgressById: Record<number, boolean>;
  sortBy: LocationListSortOptionValues;
};

export const InitialLocationsState: LocationsState = {
  allLocationIds: [],
  byId: {},
  isLoading: false,
  isEditing: false,
  hasError: false,
  canDelete: false,
  deleteInProgressById: {},
  isAddInProgress: false,
  isUpdateInProgress: false,
  sortBy: 'name',
};

const locationsSlice = createSlice({
  name: nameof<InsightsState>('locations'),
  initialState: InitialLocationsState,
  reducers: {
    loadAllLocationsStart: state => {
      state.isLoading = true;
    },
    loadAllLocationsSuccess: (state, action: PayloadAction<dto.GetGeonameModel[]>) => {
      const locations = action.payload;
      state.isLoading = false;
      state.hasError = false;
      state.byId = {};
      state.isAddInProgress = false;
      state.isUpdateInProgress = false;
      state.deleteInProgressById = {};
      locations.forEach(location => {
        state.byId[location.geonameId] = location;
        state.deleteInProgressById[location.geonameId] = false;
      });
      const sortOption = locationListSortOptionByValue(state.sortBy);
      state.allLocationIds = orderBy(locations, sortOption.keys, sortOption.orders).map(
        it => it.geonameId
      );
      state.canDelete = locations.length > 1;
    },
    loadAllLocationsError: state => {
      state.isLoading = false;
      state.hasError = true;
    },
    addLocationStart: state => {
      state.isAddInProgress = true;
    },
    addLocationError: state => {
      state.isAddInProgress = false;
    },
    updateLocationsStart: state => {
      state.isUpdateInProgress = true;
    },
    updateLocationsError: state => {
      state.isUpdateInProgress = false;
      state.hasError = true;
    },
    editLocationsStart: state => {
      state.hasError = false;
      state.isEditing = true;
    },
    editLocationsEnd: state => {
      state.isEditing = false;
    },
    deleteLocationStart: (state, action: PayloadAction<number>) => {
      if (action.payload in state.deleteInProgressById) {
        state.deleteInProgressById[action.payload] = true;
      }
    },
    deleteLocationError: (state, action: PayloadAction<number>) => {
      if (action.payload in state.deleteInProgressById) {
        state.deleteInProgressById[action.payload] = false;
      }
    },
    sortLocations: (state, action: PayloadAction<LocationListSortOptionValues>) => {
      const sortBy = action.payload;
      if (sortBy !== state.sortBy) {
        const sortOption = locationListSortOptionByValue(sortBy);
        state.allLocationIds = orderBy(
          Object.values(state.byId),
          sortOption.keys,
          sortOption.orders
        ).map(it => it.geonameId);
        state.sortBy = sortBy;
      }
    },
  },
});

const { actions, reducer } = locationsSlice;
export const {
  loadAllLocationsStart,
  loadAllLocationsSuccess,
  loadAllLocationsError,
  deleteLocationError,
  deleteLocationStart,
  addLocationStart,
  addLocationError,
  updateLocationsStart,
  updateLocationsError,
  editLocationsStart,
  editLocationsEnd,
  sortLocations,
} = actions;
export default reducer;
